import { FormattedFacet } from 'Hooks/useAlgoliaProducts';
import { SEmptyState } from '@setvi/shared/components';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, useState } from 'react';
import { Category } from 'Services/Mongo/Types';
import { CustomRefinmentList } from './custom-refinment-list';
import { GlobalLoader } from './loader';

export interface FacetsProps {
  facets: FormattedFacet[];
  attributesForFaceting: readonly string[];
  selectedFacets: string[];
  loading: boolean;
  globalSearch: string;
  onSelectFacet: (isChecked: boolean, facet: string[]) => void;
  selectedCategory: Category;
}

export interface ModifiedFacet {
  name: string;
  expanded: boolean;
  searchedResult: number;
}

export const optionsLimit = 15;
export const facetOptionHeight = 24;
export const facetMarginBottom = 12;

export const Facets = ({
  onSelectFacet,
  facets = [],
  selectedFacets,
  selectedCategory,
  globalSearch,
  loading,
  attributesForFaceting
}: FacetsProps) => {
  const [expandedAccordions, setExpandedAccordions] = useState<string[]>([]);
  const initModified = facets.map(i => ({
    name: i.name,
    expanded: false,
    searchedResult: null
  }));
  const [modifiedFacets, setModifiedFacets] =
    useState<ModifiedFacet[]>(initModified);

  const parentRef = useRef();
  const isFirstLoad = useRef(true);
  const titleHeaderHeight = 44;
  const searchHeight = 52;
  const optionHeight = 50;
  const buttonHeight = 40;
  const emptyStateHeight = 182;

  const rowVirtualizer = useVirtualizer({
    count: facets.length,
    getScrollElement: () => parentRef.current,
    estimateSize: i => {
      const isExpandedAccordion = expandedAccordions.includes(facets[i].name);
      const rowHasSearchInput =
        attributesForFaceting
          ?.find(attribute => attribute.includes(facets[i].name))
          ?.includes('searchable') || false;

      const modifiedFacet = modifiedFacets?.find(
        j => j.name === facets[i].name
      );

      const options =
        !!modifiedFacet && modifiedFacet?.searchedResult !== null
          ? modifiedFacet.searchedResult
          : facets[i].values.length;

      const getIsOverLimitAndNotExpanded = (values: number) =>
        values > optionsLimit && !modifiedFacet?.expanded;

      if (!isExpandedAccordion) return titleHeaderHeight + facetMarginBottom;

      return (
        titleHeaderHeight +
        (getIsOverLimitAndNotExpanded(options) ? optionsLimit : options) *
          optionHeight +
        (rowHasSearchInput ? searchHeight : 0) +
        (options > optionsLimit ? buttonHeight : 0) +
        facetMarginBottom +
        (options === 0 ? emptyStateHeight : 0)
      );
    },
    overscan: 0
  });

  useEffect(() => {
    rowVirtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedFacets, expandedAccordions]);

  useEffect(() => {
    const lastSelectedFacet =
      selectedFacets[selectedFacets.length - 1]?.split(':')?.[0];
    const newModifiedFacets = facets.map(i => ({
      name: i.name,
      expanded:
        i.name === lastSelectedFacet
          ? modifiedFacets.find(j => j.name === lastSelectedFacet)?.expanded
          : false,
      searchedResult: null
    }));
    setModifiedFacets(newModifiedFacets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facets]);

  // Make first accordion expanded by default when facets are loaded and remove any expanded accordion that is not in the new facets
  useEffect(() => {
    if (!facets.length) return;

    if (isFirstLoad.current) {
      setExpandedAccordions([facets[0].name]);
      isFirstLoad.current = false;
    }
    setExpandedAccordions(prev =>
      prev.filter(i => facets.map(j => j.name).includes(i))
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facets]);

  if (loading) return <GlobalLoader />;

  if (!facets.length) return <SEmptyState subTitle="No Filters Available..." />;

  return (
    <div style={{ height: '100%' }}>
      <div
        ref={parentRef}
        className="List"
        style={{
          height: '100%',
          width: '100%',
          overflowY: 'auto',
          contain: 'strict'
        }}>
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative'
          }}>
          {rowVirtualizer.getVirtualItems().map(row => (
            <div
              key={row.key}
              ref={rowVirtualizer.measureElement}
              data-index={row.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${row.start}px)`
              }}>
              <CustomRefinmentList
                facet={facets[row.index]}
                attributesForFaceting={attributesForFaceting}
                selectedFacets={selectedFacets}
                selectedCategory={selectedCategory}
                globalSearch={globalSearch}
                modifiedFacets={modifiedFacets}
                expandedAccordion={expandedAccordions.includes(
                  facets[row.index].name
                )}
                handleExpandAccordion={name =>
                  setExpandedAccordions(prev => {
                    const isClosed = prev.includes(name);

                    return isClosed
                      ? prev.filter(i => i !== name)
                      : [...prev, name];
                  })
                }
                setModifiedFacets={setModifiedFacets}
                onSelectFacet={onSelectFacet}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
